import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Something went wrong');

      setShowSuccessPopup(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-6 py-12">
      
      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-darkBg border border-white/10 p-8 rounded-3xl max-w-sm w-full text-center shadow-2xl transform transition-all animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Success!</h3>
            <p className="text-gray-400 mb-8">Account successfully created! Please login to continue.</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-accentBlue text-black rounded-2xl font-bold hover:scale-[1.02] transition-transform"
            >
              Go to Login
            </button>
          </div>
        </div>
      )}

      <div className="w-full max-w-md bg-white/5 border border-white/10 p-8 rounded-[40px] backdrop-blur-xl">
        <h2 className="text-3xl font-bold mb-8 text-center text-accentBlue flex items-center justify-center gap-2">
          <UserPlus size={28} /> Create Account
        </h2>
        {error && <div className="bg-red-500/10 border border-red-500 text-red-500 p-3 rounded-lg mb-6 text-sm text-center">{error}</div>}
        
        <form onSubmit={handleSignup} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-400 mb-2">First Name</label>
              <input 
                type="text" 
                id="firstName"
                placeholder="John" 
                onChange={handleChange}
                value={formData.firstName}
                className="w-full bg-darkBg border border-white/10 p-3 rounded-xl focus:border-accentBlue outline-none text-sm" 
                required 
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-400 mb-2">Last Name</label>
              <input 
                type="text" 
                id="lastName"
                placeholder="Doe" 
                onChange={handleChange}
                value={formData.lastName}
                className="w-full bg-darkBg border border-white/10 p-3 rounded-xl focus:border-accentBlue outline-none text-sm" 
                required 
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
            <input 
              type="email" 
              id="email"
              placeholder="you@example.com" 
              onChange={handleChange}
              value={formData.email}
              className="w-full bg-darkBg border border-white/10 p-3 rounded-xl focus:border-accentBlue outline-none text-sm" 
              required 
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="••••••••"
                onChange={handleChange}
                value={formData.password}
                className="w-full bg-darkBg border border-white/10 p-3 pr-12 rounded-xl focus:border-accentBlue outline-none text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          
          <div>
            <button 
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3 bg-accentBlue text-black rounded-2xl font-bold hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </div>
        </form>

        <p className="mt-8 text-center text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-accentBlue hover:underline">
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;